/**
 * Role-Based Access Guard
 * 
 * Protects routes by verifying user authentication and group membership.
 * Redirects to home page if user is not signed in or doesn't belong to required group.
 * 
 * Usage in routes:
 * {
 *   path: 'assessments',
 *   canActivate: [roleGuard],
 *   loadChildren: () => import('./features/assessment-list/assessment-list.routes').then(m => m.assessmentListRoutes)
 * }
 */

import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthInitService } from '../services/auth-init.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuardService implements CanActivate {
  constructor(private authInitService: AuthInitService, private router: Router) { }
  async canActivate(): Promise<boolean> {
    try {
      const isSignedIn = await this.authInitService.checkIfUserIsInGroup();
      if (isSignedIn) {
        return true;
      } else {
        this.router.navigate(['/error'], { queryParams: { type: 'forbidden' } });
        return false;
      }
    } 
    catch (error) {
      this.handleErrorResponse(error);
      return false;
    }
  }

  handleErrorResponse = (error: any) => {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        this.router.navigate(['/error'], { queryParams: { type: 'unauthorized' } });
        console.error('Unauthorized access (401):', error.error);
      }
      else if (error.status === 403) {
        this.router.navigate(['/error'], { queryParams: { type: 'forbidden' } });
        console.error('Forbidden access (403):', error.error);
      }
      else {
        this.router.navigate(['/error'], { queryParams: { type: 'server-error' } });
        console.error('Access error:', error.error);
      }
    } else {
      this.router.navigate(['/error'], { queryParams: { type: 'server-error' } });
      console.error('Unexpected error during role check:', error);
    }
  }
}
